import React, { useState } from "react";
import {
  Space,
  DatePicker,
  Button,
  Checkbox,
  Row,
  Col
} from "antd";
import "@styles/styles-Settings.css";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";
import { ActiveRosterShortcutModal } from "./ActiveRosterShortcutModal";


export const DailyActiveRoster = () => {
  const date_format = "YYYY-MM-DD";

  const { allPlayers } = useSelector(({ team }) => team);

  const [selectedDate, setSelectedDate] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerStatuses, setPlayerStatuses] = useState({});
  const [initialStatuses, setInitialStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [shortcutModalVisible, setShortcutModalVisible] = useState(false);
  const [showRetiredPlayers, setShowRetiredPlayers] = useState(false);

  const trainingAPI = new TrainingAPI();

  const getPlayerNameById = (playerId) => {
    const player = allPlayers.find(p => p.player_id === playerId);
    if (player) {
      return `${player.first_name} ${player.last_name}`;
    }
    return `Player ${playerId}`;
  };

  const onDateChange = (date) => {
    setSelectedDate(date);
    setHasSearched(false);
    setPlayers([]);
    setPlayerStatuses({});
    setInitialStatuses({});
    setShowRetiredPlayers(false);
  };

  const handleSearch = async () => {
    if (!selectedDate) {
      message.warning("Please select a date first");
      return;
    }

    setLoading(true);
    try {
      const dateString = selectedDate.format(date_format);
      const response = await trainingAPI.getActiveDatapoints(dateString);
      
      // Enrich API response with player names from Redux
      const enrichedPlayers = response.result.map(player => ({
        ...player,
        player_name: getPlayerNameById(player.player_id)
      }));
      
      // Sort players by is_active first, then alphabetically
      const sortedPlayers = [...enrichedPlayers].sort((a, b) => {
        // Sort by is_active first (active players first)
        if (a.is_active !== b.is_active) {
          return b.is_active ? 1 : -1;
        }
        
        // Then sort alphabetically by name
        return a.player_name?.localeCompare(b.player_name || "") || 0;
      });
      
      // Store the sorted players
      setPlayers(sortedPlayers);
      
      // Create status map from enriched data
      const statusMap = {};
      sortedPlayers.forEach(player => {
        statusMap[player.player_id] = {
          is_active: player.is_active,
          total_time: player.total_time,
          retired: player.retired,
          name: player.player_name
        };
      });

      setPlayerStatuses(statusMap);
      setInitialStatuses(JSON.parse(JSON.stringify(statusMap)));
      setHasSearched(true);
      message.success("Player data loaded successfully");
    } catch (err) {
      console.error(err);
      message.error("Error loading player data");
    }
    setLoading(false);
  };

  const handleCheckboxChange = (playerId) => (e) => {
    const isChecked = e.target.checked;
    setPlayerStatuses(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        is_active: isChecked
      }
    }));
  };

  const hasChanges = () => {
    if (!hasSearched) return false;
    
    return Object.keys(playerStatuses).some(playerId => {
      return playerStatuses[playerId].is_active !== initialStatuses[playerId]?.is_active;
    });
  };

  const getChangedPlayerIds = () => {
    const changedIds = [];
    Object.keys(playerStatuses).forEach(playerId => {
      if (playerStatuses[playerId].is_active !== initialStatuses[playerId]?.is_active) {
        changedIds.push(parseInt(playerId));
      }
    });
    return changedIds;
  };

  const handleSaveChanges = async () => {
    if (!selectedDate) {
      message.warning("Please select a date first");
      return;
    }

    const changedPlayerIds = getChangedPlayerIds();
    if (changedPlayerIds.length === 0) {
      message.info("No changes to save");
      return;
    }

    setSaveLoading(true);
    try {
      const dateString = selectedDate.format(date_format);

      // Group players by their new status
      const activatedPlayers = [];
      const deactivatedPlayers = [];
      
      changedPlayerIds.forEach(playerId => {
        if (playerStatuses[playerId].is_active) {
          activatedPlayers.push(playerId);
        } else {
          deactivatedPlayers.push(playerId);
        }
      });

      // Make API calls for activated and deactivated players separately
      const promises = [];
      
      if (activatedPlayers.length > 0) {
        promises.push(
          trainingAPI.updateActiveDatapoints(
            activatedPlayers,
            dateString,
            dateString,
            true
          )
        );
      }
      
      if (deactivatedPlayers.length > 0) {
        promises.push(
          trainingAPI.updateActiveDatapoints(
            deactivatedPlayers,
            dateString,
            dateString,
            false
          )
        );
      }

      await Promise.all(promises);
      
      // Update initial statuses to match current statuses after successful save
      setInitialStatuses(JSON.parse(JSON.stringify(playerStatuses)));
      
      message.success("Changes saved successfully");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 400) {
        message.error(err.response.data?.error || "Invalid request");
      } else {
        message.error("Error saving changes");
      }
    }
    setSaveLoading(false);
  };

  const handleUseShortcut = () => {
    setShortcutModalVisible(true);
  };

  // Split players into retired and non-retired (already sorted from API response)
  const nonRetiredPlayers = players.filter(p => !p.retired);
  const retiredPlayers = players.filter(p => p.retired);

  // Render player list helper function
  const renderPlayerList = (playerList) => {
    return playerList.map(player => {
      const status = playerStatuses[player.player_id];
      const initialStatus = initialStatuses[player.player_id];
      if (!status) return null;
      
      // Check if player has no data on this date
      const hasNoData = player.total_time === 0;
      
      // Gray out retired players based on their saved (initial) status, not current checkbox state
      const isRetiredAndInactive = player.retired && !initialStatus?.is_active;
      
      return (
        <Col 
          key={player.player_id} 
          xs={24} 
          sm={12} 
          md={8} 
          lg={6}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            opacity: isRetiredAndInactive ? 0.6 : 1
          }}>
            <Checkbox
              checked={status.is_active}
              onChange={handleCheckboxChange(player.player_id)}
              disabled={loading || saveLoading || hasNoData}
            >
              <span style={{ 
                color: isRetiredAndInactive ? '#999' : 'inherit'
              }}>
                {player.player_name}
              </span>
              {player.total_time > 0 ? (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#888',
                  marginLeft: 4
                }}>
                  ({Math.round(player.total_time)} min)
                </span>
              ) : (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#999',
                  marginLeft: 4
                }}>
                  (no data)
                </span>
              )}
            </Checkbox>
          </div>
        </Col>
      );
    });
  };

  return (
    <div>
        <div className="team-settings-input-wrap settings-first-field settings-inline-field">
          <div className="team-settings-input-label">Date</div>
          <Space wrap>
            <DatePicker
              onChange={onDateChange}
              value={selectedDate}
              style={{ width: 250 }}
              placeholder="Select a date"
            />
            <Button
              onClick={handleSearch}
              loading={loading}
              disabled={!selectedDate}
            >
              Search
            </Button>
            <Button onClick={handleUseShortcut}>
              Update multiple days
            </Button>
          </Space>
        </div>

        {hasSearched && (
          <div className="settings-row">
            {/* Non-Retired Players Section */}
            {nonRetiredPlayers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div className="report-injury-header2 settings-first-header" style={{ marginBottom: 16 }}>Active Roster</div>
                <div style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto',
                  paddingRight: '8px',
                  marginBottom: '16px'
                }}>
                  <Row gutter={[16, 8]}>
                    {renderPlayerList(nonRetiredPlayers)}
                  </Row>
                </div>
              </div>
            )}
            
            {/* Retired Players Section */}
            {retiredPlayers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Button 
                  type="link" 
                  onClick={() => setShowRetiredPlayers(!showRetiredPlayers)}
                  style={{ padding: 0, marginBottom: '16px' }}
                >
                  {showRetiredPlayers ? 'Hide' : 'Show'} retired players ({retiredPlayers.length})
                </Button>
                
                {showRetiredPlayers && (
                  <div style={{ 
                    maxHeight: '500px', 
                    overflowY: 'auto',
                    paddingRight: '8px',
                    marginBottom: '16px'
                  }}>
                    <Row gutter={[16, 8]}>
                      {renderPlayerList(retiredPlayers)}
                    </Row>
                  </div>
                )}
              </div>
            )}
            
            {/* Save Changes Button */}
            <div className="injury-item">
              <Button 
                loading={saveLoading}
                type="primary"
                onClick={handleSaveChanges}
                disabled={!hasChanges()}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}

        <ActiveRosterShortcutModal
          visible={shortcutModalVisible}
          onClose={() => setShortcutModalVisible(false)}
          players={players}
          onSuccess={() => {
            // Optionally refresh the current date's data
            if (selectedDate) {
              handleSearch();
            }
          }}
        />
    </div>
  );
};
